import {html, LitElement} from 'lit';
import {customElement, state, property, queryAssignedElements, query} from 'lit/decorators.js';
import {tablet} from '../../js/services/const';
import {Logger, PubSub} from '@mamdev/cad.static-next-lib';
import styles from './starpreview.styles';
import Tracking2 from '../../ts/services/tracking2';

@customElement('ui-starpreview')
export class Starpreview extends LitElement {
  static styles = styles;
  @property() name: string;
  @property() occupation: string;
  @property() birthdate: string;
  @property() srcimage = '';

  @state() leftOffset = 0;
  @state() topOffset = 0;
  @state() mouseOver = false;
  @state() linkTarget = '#';
  COREMID: string;
  brainZoneID: string;
  componentPath: string;

  @query('#mobileDialog') mobileDialogElement: HTMLDialogElement;
  @query('.pop') popup!: HTMLDivElement;
  @queryAssignedElements({slot: undefined, selector: 'a'}) anchor!: HTMLAnchorElement[];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  handleWindowScroll() {
    if (this.mouseOver) {
      this.sendCloseBrainTrackingEvent();
      this.mouseOver = false;
    }
  }

  handleMouseEnter() {
    if (this.isMobileViewport) return;

    this.mouseOver = true;

    this.updateComplete.then(() => {
      this.sendOpenBrainTrackingEvent();

      PubSub.subscribeOnce('windowScrolling', () => {
        this.handleWindowScroll();
      });

      const targetRectangle = this.getBoundingClientRect();
      this.topOffset = targetRectangle.bottom + this.popup.clientHeight >= window.innerHeight ? targetRectangle.top - this.popup.clientHeight : targetRectangle.bottom;
      this.leftOffset = targetRectangle.left + this.popup.clientWidth >= window.innerWidth ? (targetRectangle.right - this.popup.clientWidth) : targetRectangle.left;
    })
      .catch(e => Logger.logUnknownError(e, 'ui-starpreview'));
  }

  handleMouseLeave() {
    if (!this.mouseOver) return;
    this.sendCloseBrainTrackingEvent();
    this.mouseOver = false;
  }

  handleSlotChange() {
    this.linkTarget = this.anchor[0].href;
    this.componentPath = this.anchor[0].dataset.componentPath ?? '';

    if (this.anchor[0].dataset.brainZone) {
      const brainZone = this.anchor[0].dataset.brainZone;
      const brainZoneParts = brainZone.split('.');
      this.COREMID = brainZoneParts[1];
      this.brainZoneID = brainZoneParts[brainZoneParts.length - 1].split('_')[1];
    }
  }

  openMobileDialog(event: Event) {
    if (!this.isMobileViewport || !this.isDialogElementSupported) {
      return;
    }

    try {
      this.mobileDialogElement.showModal();
      this.sendOpenBrainTrackingEvent();
      event.preventDefault();
    } catch (e) {
      Logger.logUnknownError(e, 'starpreview');
    }
  }

  openTextlink(event: Event) {
    this.trackBrainZone(event);

    if (this.isMobileViewport) {
      this.mobileDialogElement.close();
    }
  }

  closeMobileDialog(event: Event) {
    this.mobileDialogElement.close();
    this.sendCloseBrainTrackingEvent();
    event.preventDefault();
  }

  render() {
    return html`
      <span class="wrapper" @click=${this.openMobileDialog}>
        <slot @slotchange="${this.handleSlotChange}"></slot>
      </span>
      ${this.isMobileViewport ? this.mobileDialogTemplate() : this.desktopPopupTemplate()}
    `;
  }

  mobileDialogTemplate() {
    return this.isDialogElementSupported ? html`
      <dialog id="mobileDialog">
        <div class="pop__title">
          <div class="pop__name"><strong>${this.name}</strong></div>
          <button tabindex="0" class="close__container" @click=${this.closeMobileDialog}>
            <span class="close__leftright"></span>
            <span class="close__rightleft"></span>
          </button>
        </div>
        ${this.contentTemplate()}
      </dialog>
      ` : null;
  }

  desktopPopupTemplate() {
    return this.mouseOver ? html`
      <div class="pop" style="top: ${this.topOffset}px; left: ${this.leftOffset}px;">
        <div class="pop__title">
          <div class="pop__name"><strong>${this.name}</strong></div>
        </div>
          ${this.contentTemplate()}
      </div>
    ` : null;
  }

  contentTemplate() {
    return html`
      <div class="pop__image">
        <div class="pop__bg" style="background-image: url(${this.srcimage});"></div>
        <img alt="${this.name}" src="${this.srcimage}" />
      </div>
      <div class="pop__beruf">
        <strong>Beruf:</strong> ${this.occupation}<br />
        <strong>Geburtstag:</strong> ${this.birthdate}
      </div>
      <div class="link__wrapper">
        <a tabindex="1" @click=${this.openTextlink} href="${this.linkTarget}">mehr Informationen zur Person</a>
      </div>
    `;
  }

  sendOpenBrainTrackingEvent() {
    PubSub.publish('starpreview:openOrCloseEvent', {
      referrer: '&referrer=undefined',
      click1: '&click1=' + this.COREMID,
      click2: '&click2=card',
      click3: `&click3=open_${this.brainZoneID}`,
      click4: '&click4=1',
      clickname: '&clickname=card_open'
    });

    Tracking2.callTealiumLink({
      businessEventType: 'userAction',
      componentPath: `${this.componentPath}`,
      componentName: 'identitycard',
      componentLabel: this.name,
      componentResultState: 'opened',
      elementTagName: 'div',
      eventType: 'mouseover'
    });

    // Fired when the popup is displayed
    Tracking2.callTealiumLink({
      businessEventType: 'expose',
      componentPath: `${this.componentPath}.ui-starpreview.1`,
      componentName: 'identitycard',
      componentLabel: this.name
    });
  }

  sendCloseBrainTrackingEvent() {
    PubSub.publish('starpreview:openOrCloseEvent', {
      referrer: '&referrer=undefined',
      click1: '&click1=' + this.COREMID,
      click2: '&click2=card',
      click3: `&click3=close_${this.brainZoneID}`,
      click4: '&click4=1',
      clickname: '&clickname=card_close'
    });

    Tracking2.callTealiumLink({
      businessEventType: 'userAction',
      componentPath: `${this.componentPath}`,
      componentName: 'identitycard',
      componentLabel: this.name,
      componentResultState: 'closed',
      elementTagName: 'div',
      eventType: 'mousedown'
    });
  }

  trackBrainZone(e: Event) {
    const target = e.target as HTMLLinkElement;
    const brainData = `.${this.COREMID}.card.textlink_${this.brainZoneID}.1`;
    PubSub.publish('starpreview:textlink1', {
      targetUrl: this.linkTarget,
      brainData: brainData
    });

    Tracking2.callTealiumLink({
      businessEventType: 'userAction',
      componentPath: `${this.componentPath}.ui-starpreview.1.link.1`,
      componentName: 'identitycard',
      componentLabel: target.innerText || '',
      elementTagName: 'a',
      eventType: 'mousedown',
      targetUrl: this.linkTarget
    });
  }

  get isDialogElementSupported(): boolean {
    return typeof HTMLDialogElement === 'function';
  }

  get isMobileViewport(): boolean {
    return window.innerWidth < tablet;
  }
}
